import { InformationCircleIcon, XIcon } from '@heroicons/react/outline';
import classNames from 'classnames';
import { FC, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import {
  PatientTableDataFragment,
  ProgramTagDataFragment,
  useAutomationRulesQuery,
  useRemoveProgramTagMutation,
} from '../../../../../generated/graphql';

import ModalDialog from '../../../../components/ModalDialog';
import ToastAlert from '../../../../components/ToastAlert';
import ProgramTagSelectDropdown from '../../../../components/ProgramTag/ProgramTagSelectDropdown';
import { DEFAULT_TAG_COLOR, getTagStyles } from '../../../../lib/colors';
import { isTagUsedAsAutomationTrigger } from '../../../../lib/automationHelpers';

type BulkRemoveTagModalProps = {
  isOpen: boolean;
  onClose: () => void;
  selectedPatients: PatientTableDataFragment[];
  providerProgramTags: ProgramTagDataFragment[];
  onSuccess: () => void | Promise<void>;
};

const BulkRemoveTagModal: FC<BulkRemoveTagModalProps> = ({
  isOpen,
  onClose,
  selectedPatients,
  providerProgramTags,
  onSuccess,
}) => {
  const [selectedTags, setSelectedTags] = useState<ProgramTagDataFragment[]>(
    [],
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [removeProgramTagMutation] = useRemoveProgramTagMutation();
  const { data: automationRulesData } = useAutomationRulesQuery({
    variables: { limit: 100 },
    skip: !isOpen,
  });

  useEffect(() => {
    if (!isOpen) {
      setSelectedTags([]);
    }
  }, [isOpen]);

  const tagsOnSelectedPatients = useMemo(() => {
    const tagIds = new Set<string>();
    for (const patient of selectedPatients) {
      for (const tag of patient.tags ?? []) {
        tagIds.add(tag.id);
      }
    }
    return providerProgramTags.filter((t) => tagIds.has(t.id));
  }, [selectedPatients, providerProgramTags]);

  const handleTagSelected = (tag: ProgramTagDataFragment) => {
    if (!selectedTags.some((t) => t.id === tag.id)) {
      setSelectedTags((prev) => [...prev, tag]);
    }
  };

  const handleRemoveTag = (tagId: string) => {
    setSelectedTags((prev) => prev.filter((t) => t.id !== tagId));
  };

  const selectedTagIdsUsedAsAutomationTrigger = selectedTags
    .filter((t) =>
      isTagUsedAsAutomationTrigger(
        t.id,
        automationRulesData?.automationRules ?? [],
      ),
    )
    .map((t) => t.id);
  const showAutomationWarning =
    selectedTagIdsUsedAsAutomationTrigger.length > 0;

  const handleSubmit = async () => {
    if (selectedTags.length === 0 || selectedPatients.length === 0) return;

    setIsSubmitting(true);

    const ops: { programId: string; promise: Promise<{ ok: boolean }> }[] = [];
    for (const patient of selectedPatients) {
      const programId = patient.programInstanceId;
      if (!programId) continue;
      const patientTagIds = new Set((patient.tags ?? []).map((t) => t.id));

      for (const tag of selectedTags) {
        if (!patientTagIds.has(tag.id)) continue;

        ops.push({
          programId,
          promise: removeProgramTagMutation({
            variables: {
              input: { programId, programTagId: tag.id },
            },
          })
            .then(() => ({ ok: true }))
            .catch(() => ({ ok: false })),
        });
      }
    }

    if (ops.length === 0) {
      setIsSubmitting(false);
      toast.custom(({ visible }) => (
        <ToastAlert
          isVisible={visible}
          level="error"
          message="No tags to remove — the selected tags are not assigned to any of the selected clients."
        />
      ));
      return;
    }

    const results = await Promise.all(ops.map((o) => o.promise));
    const successCount = results.filter((r) => r.ok).length;
    const failCount = results.length - successCount;

    const affectedPatientIds = new Set<string>();
    results.forEach((r, i) => {
      if (r.ok) affectedPatientIds.add(ops[i].programId);
    });
    const affectedCount = affectedPatientIds.size;

    if (failCount > 0) {
      toast.custom(({ visible }) => (
        <ToastAlert
          isVisible={visible}
          level="error"
          message={
            successCount === 1
              ? `1 tag application succeeded; ${failCount} could not be removed.`
              : `${successCount} tag applications succeeded; ${failCount} could not be removed.`
          }
        />
      ));
    } else {
      toast.custom(({ visible }) => (
        <ToastAlert
          isVisible={visible}
          level="success"
          message={`Tag${
            selectedTags.length > 1 ? 's' : ''
          } removed from ${affectedCount} ${
            affectedCount === 1 ? 'client' : 'clients'
          }`}
        />
      ));
    }

    try {
      await onSuccess();
    } finally {
      setIsSubmitting(false);
      setSelectedTags([]);
      onClose();
    }
  };

  const canSubmit =
    selectedTags.length > 0 && selectedPatients.length > 0 && !isSubmitting;

  return (
    <ModalDialog
      title="Remove tags from clients"
      isOpen={isOpen}
      setClosed={onClose}
      primaryActionTitle={`Remove tag${
        selectedTags.length > 1 ? 's' : ''
      } from ${selectedPatients.length} ${
        selectedPatients.length === 1 ? 'client' : 'clients'
      }`}
      primaryActionOnClick={handleSubmit}
      primaryActionDisabled={!canSubmit}
      primaryActionProps={{ theme: 'destructive' }}
      fetching={isSubmitting}
      width="medium"
      overflow="auto"
      fullHeight
    >
      <div className="space-y-4">
        <div>
          <ProgramTagSelectDropdown
            programTags={tagsOnSelectedPatients}
            value=""
            onChange={() => undefined}
            onTagSelected={handleTagSelected}
            id="bulk-remove-tag-select"
            label="Select tags to remove"
            placeholder="Search tags"
            excludeTagIds={selectedTags.map((t) => t.id)}
            onError={(msg) => toast.error(msg)}
          />
          {selectedTags.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {selectedTags.map((tag) => {
                const styles = getTagStyles(tag.color ?? DEFAULT_TAG_COLOR);
                return (
                  <span
                    key={tag.id}
                    className={classNames(
                      'text-sm inline-flex items-center gap-1 rounded-full px-2 py-1',
                    )}
                    style={styles}
                  >
                    {tag.name}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag.id)}
                      className="rounded-full p-0.5 hover:opacity-80"
                      aria-label={`Remove ${tag.name}`}
                    >
                      <XIcon className="h-3.5 w-3.5" />
                    </button>
                  </span>
                );
              })}
            </div>
          )}
        </div>

        {showAutomationWarning && (
          <div className="flex rounded-md bg-orange-25/30 p-4 text-orange-125">
            <InformationCircleIcon
              className="h-5 min-h-[20px] w-5 min-w-[20px] shrink-0 text-orange-100"
              aria-hidden
            />
            <span className="ml-3 font-sans text-caption font-medium">
              One or more selected tags trigger automations. Removing them from
              these clients may run those automations for each client.
            </span>
          </div>
        )}
      </div>
    </ModalDialog>
  );
};

export default BulkRemoveTagModal;
